// SPDX-License-Identifier: GPL-3.0-or-later
// The game page: it wires the engine, the renderer and the table "SigmaShake Pinball" into one page.
//
// Flow: start screen -> start -> play (keyboard, pad or touch) -> game over -> play again.
// The featured-turn flow is optional and never blocks free play: Twitch sign-in or a claim code,
// the waiting list, then the featured WebSocket that sends the game status.
// This file only wires. The rules live in the modules it calls:
// game-flow.ts (start, pause, end, loop), dialog-policy.ts (which dialog opens when),
// featured-panel.ts (the Featured turns dialog), key-settings.ts (the key list), layout.ts.
// The feedback (effects, sound, music, popups, touch, stream background) is wired from three
// modules: feedback-core.ts (one bus, one settings store, one flash limiter), feedback-layers.ts
// (the layers) and touch-controls.ts. The /overlay bundle never imports them.
// The pixel ratio is capped (`capPixelRatio`). Every user string goes in with `textContent`.

import { Rng } from "../src/engine/core/rng.ts";
import { createEngine } from "../src/engine/index.ts";
import { capPixelRatio, createPinballRenderer, type PinballRenderer } from "../src/render/index.ts";
import {
  type InputEvent,
  type PinballEngine,
  type TableSpec,
  TURN_RULE_LINES,
} from "../src/shared/contracts.ts";
import { createDecorationRegistry, makeCharacterEnv } from "../src/table/characters/index.ts";
import { buildHowToCard, HOW_TO_GOALS, HOW_TO_RUN, scoreLines } from "../src/table/howto.ts";
import { createVibe01Rules } from "../src/table/rules/vibe-01.ts";
import { createRunDirector, type RunDirector } from "../src/table/run/director.ts";
import { emptyStacks } from "../src/table/run/perks.ts";
import type { BossState, RunSave, RunView } from "../src/table/run/types.ts";
import { loadVibe01Table } from "../src/table/table.ts";
import { audioContextFactory } from "./audio-layer.ts";
import { type DialogId, menuButtonAction, openSet } from "./dialog-policy.ts";
import {
  closeDialog,
  type Dialogs,
  findDialogs,
  modalOpen,
  openDialog,
  setAfterOpenHook,
  wireDialogs,
} from "./dialogs.ts";
import { asBar, asList, asText, fillList, slot } from "./dom.ts";
import {
  createDraftPanel,
  type DraftDoc,
  type DraftPanel,
  type DraftPanelDeps,
  type DraftViewLike,
} from "./draft-panel.ts";
import { createFeaturedPanel, type FeaturedPanel } from "./featured-panel.ts";
import { wantsClaimDialog } from "./featured-view.ts";
import { createFeedbackCore, type FeedbackCore, platformOf } from "./feedback-core.ts";
import { createFeedbackLayers, type FeedbackLayers } from "./feedback-layers.ts";
import { bindFeedbackSettings } from "./feedback-settings.ts";
import { createGameFlow, draftRouter, type FlowRunPort, type GameFlow } from "./game-flow.ts";
import { createGamepadController, type GamepadController, padsFrom } from "./gamepad.ts";
import {
  createBossHud,
  createHud,
  createLiveFeed,
  createRunHud,
  type Hud,
  type LiveFeed,
} from "./hud.ts";
import { bindKeySettings } from "./key-settings.ts";
import type { StorageLike } from "./keybinds.ts";
import { createKeyboardController, type KeyboardController, type KeyCommand } from "./keys.ts";
import { canvasSize } from "./layout.ts";
import { createLevelSelect } from "./level-select.ts";
import { bindPageMenu } from "./menu.ts";
import { createPowerGauge, type PowerGauge } from "./power.ts";
import type { StreamLayerDeps } from "./stream-layer.ts";
import { routeLiveText } from "./text-cues.ts";
import {
  createTouchControls,
  type TouchContainer,
  type TouchControls,
  type TouchSurface,
} from "./touch-controls.ts";

/** Key of the reduced motion choice in local storage. */
const REDUCED_KEY = "pinball.reduced_motion";

type Win = Window & typeof globalThis;
type Btn = HTMLButtonElement;
type Finder = <T extends HTMLElement = HTMLElement>(name: string) => T;

/** Read the saved reduced motion choice. A missing value follows the system setting. */
function initialReducedMotion(win: Win): boolean {
  try {
    const saved = win.localStorage.getItem(REDUCED_KEY);
    if (saved === "1") return true;
    if (saved === "0") return false;
  } catch {
    // Local storage can be blocked. Follow the system setting.
  }
  return win.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
}

/** A random seed for one page session. The engine itself uses only this seed. */
function newSeed(win: Win): number {
  const words = new Uint32Array(1);
  win.crypto.getRandomValues(words);
  return words[0] ?? 1;
}

/** View the stage as the game surface of the touch controls. */
function asSurface(el: Element): TouchSurface {
  return el as unknown as TouchSurface;
}

/** View the playfield as the box that holds the touch controls. */
function asContainer(el: Element): TouchContainer {
  return el as unknown as TouchContainer;
}

/** View the page body as the host of the stream layer. */
function asStreamHost(el: Element): StreamLayerDeps["host"] {
  return el as unknown as StreamLayerDeps["host"];
}

/** View the document as the document of the draft panel. */
function asDraftDoc(doc: Document): DraftDoc {
  return doc as unknown as DraftDoc;
}

/** View the playfield as the parent of the draft panel. */
function asDraftParent(el: Element): DraftPanelDeps["parent"] {
  return el as unknown as DraftPanelDeps["parent"];
}

/** The seed of the draft random source is the game seed mixed with this fixed word. */
const DRAFT_SEED_MIX = 0x5eedd4af;

/** The run HUD rows change only a few times each second: the page draws them at each 4th frame. */
const RUN_HUD_EVERY = 4;

/** What the panel gets when no game runs: it hides. */
const NO_DRAFT: DraftViewLike = {
  phase: "idle",
  stageIndex: 0,
  stacks: emptyStacks(),
  draft: { choices: [null, null, null], count: 0, elapsedMs: 0, autoPick: false },
};

/** What the boss bar gets when no game runs: it hides. */
const NO_BOSS: Readonly<BossState> = {
  active: false,
  name: "",
  hp: 0,
  maxHp: 0,
  phase: 0,
  shielded: false,
  telegraph: 0,
  anchor: "",
};

/** The run of the page: the director, the HUD rows, the draft panel and the level select. */
interface RunParts {
  /** Make the director of a new engine. It becomes the run now. */
  readonly newRun: (seed: number) => RunDirector;
  /** The run now (read only), or null before the first engine. */
  readonly view: () => RunView | null;
  readonly draft: DraftPanel;
  /** The port of the game flow. */
  readonly port: FlowRunPort;
  /** Draw the level select from a save. */
  readonly showLevels: (save: RunSave) => void;
  /** Put the focus on a draft card again (after a pause or a tab switch). */
  readonly refocusDraft: () => void;
}

interface RunPartsDeps {
  readonly doc: Document;
  readonly q: Finder;
  readonly core: Pick<FeedbackCore, "bus">;
  /** True when no game runs: the draft panel hides then, also when the run phase is "draft". */
  readonly isIdle: () => boolean;
  /** True while a modal dialog is open: the keys of the draft panel stay off then. */
  readonly modalOpen: () => boolean;
}

/** Make the run parts. */
function createRunParts(deps: RunPartsDeps): RunParts {
  const { doc, q, core } = deps;
  let current: RunDirector | null = null;
  let featured = false;
  let saved: RunSave | null = null;
  let hudFrame = 0;
  const select = createLevelSelect<HTMLButtonElement>(q("level-select"));
  const runHud = createRunHud({
    stage: asText(q("hud-stage")),
    stageGoal: asText(q("hud-stage-goal")),
    event1: asText(q("hud-event-1")),
    event2: asText(q("hud-event-2")),
    event3: asText(q("hud-event-3")),
  });
  const bossHud = createBossHud({
    root: asText(q("boss")),
    name: asText(q("boss-name")),
    hp: asText(q("boss-hp")),
    bar: asBar(q("boss-bar")),
    state: asText(q("boss-state")),
  });
  const draft = createDraftPanel({
    doc: asDraftDoc(doc),
    parent: asDraftParent(q("playfield")),
    modalOpen: deps.modalOpen,
    port: { pick: (index) => current?.pick(index) ?? false },
  });
  // A click on a stage button changes the choice first (the button), then this listener reads it.
  q("level-select").addEventListener("click", () =>
    current?.setStartStage(select.startStage(false)),
  );
  const showLevels = (save: RunSave): void => {
    saved = save;
    select.render(save, featured);
  };
  return {
    newRun(seed) {
      const run = createRunDirector({
        draftRng: new Rng((seed ^ DRAFT_SEED_MIX) >>> 0),
        feedback: core.bus,
        startStage: select.startStage(false),
        featured,
      });
      current = run;
      return run;
    },
    view: () => current?.view() ?? null,
    draft,
    port: {
      setFeatured(on) {
        featured = on;
        current?.setFeatured(on);
        if (saved !== null) select.render(saved, featured);
      },
      frame() {
        const view = current?.view();
        if (view === undefined) return;
        // In attract the run rows stay as they are: the director shows no run there.
        if (deps.isIdle()) {
          hudFrame = 0;
          draft.render(NO_DRAFT);
          bossHud.update(NO_BOSS);
          return;
        }
        bossHud.update(view.boss);
        if (hudFrame === 0) runHud.updateRun(view);
        hudFrame = (hudFrame + 1) % RUN_HUD_EVERY;
        draft.render(view);
      },
      // The draft holds the engine. The flow sends no new press until the pick.
      holds() {
        return current?.view().phase === "draft";
      },
    },
    showLevels,
    refocusDraft() {
      if (!draft.shown) return;
      doc.querySelector<HTMLElement>(".draft-card:not([hidden])")?.focus();
    },
  };
}

/** Local storage, or null when the browser blocks it. */
function storageOf(win: Win): StorageLike | null {
  try {
    return win.localStorage;
  } catch {
    return null;
  }
}

/** The renderer, or null when the browser has no WebGL 2. */
function createView(
  canvas: HTMLCanvasElement,
  table: TableSpec,
  motion: { reduced: boolean },
): PinballRenderer | null {
  try {
    const decorations = createDecorationRegistry(makeCharacterEnv(() => motion.reduced));
    const view = createPinballRenderer(canvas, table, decorations);
    view.setReducedMotion(motion.reduced);
    return view;
  } catch {
    return null;
  }
}

/** The HUD and the live region. */
function createHudAndFeed(q: Finder): { readonly hud: Hud; readonly feed: LiveFeed } {
  const hud = createHud({
    score: asText(q("hud-score")),
    ball: asText(q("hud-ball")),
    multiplier: asText(q("hud-mult")),
    mode: asText(q("hud-mode")),
    goal: asText(q("hud-goal")),
    timerText: asText(q("hud-timer-text")),
    timerBar: asBar(q("hud-timer-bar")),
    saverText: asText(q("hud-saver-text")),
    saverBar: asBar(q("hud-saver-bar")),
    callout: asText(q("callout")),
  });
  return { hud, feed: createLiveFeed(asText(q("live"))) };
}

/** The how-to-play card (built from the table rules) and the turn rule lines. */
function fillHowTo(q: Finder): void {
  const rules = createVibe01Rules();
  const card = buildHowToCard(rules);
  q("howto-title").textContent = card.title;
  fillList(asList(q("howto-controls")), card.controls);
  fillList(asList(q("howto-goals")), [...HOW_TO_GOALS, ...HOW_TO_RUN]);
  fillList(asList(q("howto-turnrule")), card.turn_rule);
  fillList(
    asList(q("howto-modes")),
    card.modes.map((m) => `${m.label}: ${m.description}`),
  );
  fillList(asList(q("howto-scores")), scoreLines(rules));
  fillList(asList(q("feat-rule")), TURN_RULE_LINES);
}

/** The inputs of the page. */
interface Inputs {
  readonly keyboard: KeyboardController;
  readonly gamepad: GamepadController;
  readonly touch: TouchControls;
  readonly releaseAll: () => void;
}

interface InputDeps {
  readonly doc: Document;
  readonly win: Win;
  readonly q: Finder;
  readonly stage: HTMLElement;
  readonly dialogs: Dialogs;
  /** True while the alert dialog is on show: the keys and the pad stay off then. */
  readonly alertOpen: () => boolean;
  /** The flow comes after the inputs, so the inputs ask for it when an input arrives. */
  readonly flow: () => GameFlow;
  readonly commandFor: (code: string) => KeyCommand | null;
  /** The feedback core: it watches each input (flipper and plunger) and holds the settings. */
  readonly core: Pick<FeedbackCore, "observeInput" | "store">;
  /** The player taps the start button of the touch controls. */
  readonly onStart: () => void;
  /** The draft route: true when the draft panel took the input. */
  readonly draftRoute: (ev: InputEvent) => boolean;
}

/** Make the keyboard, the pad and the touch controls, and bind them to the document. */
function createInputs(deps: InputDeps): Inputs {
  const { doc, win, q, dialogs, flow, core } = deps;
  const modal = (): boolean => modalOpen(dialogs) || deps.alertOpen();
  const send = (ev: InputEvent): void => {
    // While the draft shows, the flippers and the plunger belong to the draft panel.
    if (deps.draftRoute(ev)) return;
    // The feedback sees the input first, so a flipper sound starts with the press.
    core.observeInput(ev);
    flow().send(ev);
  };
  const keyboard = createKeyboardController({
    send,
    onPause: () => flow().togglePause(),
    modalOpen: modal,
    commandFor: deps.commandFor,
  });
  const gamepad = createGamepadController({
    send,
    // The Menu button must not close the pause dialog under an open How to play, Settings or Featured dialog.
    onPause: () => {
      if (menuButtonAction(openSet(dialogs)) === "toggle_pause") flow().togglePause();
    },
    onStart: () => flow().startFromPad(),
    modalOpen: modal,
    pauseOpen: () => dialogs.pause.open,
    pads: () => padsFrom(win.navigator),
  });
  win.addEventListener("gamepaddisconnected", () => gamepad.releaseAll());
  // The touch controls build their own zones and buttons in the playfield (they are in no HTML).
  const touch = createTouchControls({
    surface: asSurface(deps.stage),
    container: asContainer(q("playfield")),
    send,
    settings: core.store,
    onStart: deps.onStart,
  });
  doc.addEventListener("keydown", (e) => keyboard.keyDown(e));
  doc.addEventListener("keyup", (e) => keyboard.keyUp(e));
  const releaseAll = (): void => {
    keyboard.releaseAll();
    touch.releaseAll();
    gamepad.releaseAll();
  };
  return { keyboard, gamepad, touch, releaseAll };
}

/**
 * The reduced motion choice: the page attribute, the renderer, the effects, the power bar and the
 * saved value. When it is on, the effects are reduced even if their level is "full".
 */
function bindReducedMotion(
  doc: Document,
  win: Win,
  box: HTMLInputElement,
  motion: { reduced: boolean },
  parts: {
    readonly view: PinballRenderer;
    readonly gauge: PowerGauge;
    readonly layers: Pick<FeedbackLayers, "setReducedMotion">;
  },
): void {
  const apply = (): void => {
    doc.documentElement.setAttribute("data-reduced-motion", String(motion.reduced));
    parts.view.setReducedMotion(motion.reduced);
    parts.layers.setReducedMotion(motion.reduced);
    parts.gauge.setReducedMotion(motion.reduced);
  };
  box.addEventListener("change", () => {
    motion.reduced = box.checked;
    apply();
    try {
      win.localStorage.setItem(REDUCED_KEY, motion.reduced ? "1" : "0");
    } catch {
      // The choice stays for this page only.
    }
  });
  apply();
}

/** The canvas fills the playfield under the HUD; the pixel ratio is capped. */
function bindResize(
  win: Win,
  field: HTMLElement,
  view: PinballRenderer,
  flow: () => GameFlow,
): void {
  const resize = (): void => {
    const size = canvasSize(field.getBoundingClientRect());
    view.resize(size.width, size.height, capPixelRatio(win.devicePixelRatio));
    flow().redraw();
  };
  new win.ResizeObserver(resize).observe(field);
  resize();
}

/**
 * The buttons of the page. A dialog that opens during play pauses the game first. The two buttons
 * that start a game are a user gesture: `gesture` unlocks the audio before the game starts.
 */
function bindButtons(
  q: Finder,
  dialogs: Dialogs,
  flow: GameFlow,
  panel: FeaturedPanel,
  gesture: () => void,
): void {
  const startGame = (): void => {
    gesture();
    flow.startFree();
  };
  const fromMenu = (id: DialogId, open: () => void): (() => void) => {
    return () => {
      flow.pauseFor(id, "menu");
      open();
    };
  };
  q<Btn>("btn-pause").addEventListener("click", () => flow.pause());
  q<Btn>("btn-howto").addEventListener(
    "click",
    fromMenu("howto", () => openDialog(dialogs.howto)),
  );
  q<Btn>("btn-settings").addEventListener(
    "click",
    fromMenu("settings", () => openDialog(dialogs.settings)),
  );
  q<Btn>("btn-featured").addEventListener(
    "click",
    fromMenu("featured", () => panel.open()),
  );
  q<Btn>("start-go").addEventListener("click", startGame);
  q<Btn>("start-howto").addEventListener("click", () => openDialog(dialogs.howto));
  q<Btn>("start-featured").addEventListener("click", () => panel.open());
  q<Btn>("pause-resume").addEventListener("click", () => closeDialog(dialogs.pause));
  q<Btn>("pause-howto").addEventListener("click", () => openDialog(dialogs.howto));
  q<Btn>("pause-end").addEventListener("click", () => flow.endGame());
  q<Btn>("over-again").addEventListener("click", startGame);
  q<Btn>("over-featured").addEventListener("click", () => panel.open());
}

/** The heading of the alert for a game error. */
export const GAME_STOPPED_TITLE = "The game stopped";

/** The heading of the alert when the graphics context of the canvas is lost. */
export const CONTEXT_LOST_TITLE = "The graphics stopped";

/** The text of the alert when the graphics context of the canvas is lost. */
export const CONTEXT_LOST_TEXT = "Reload the page to play.";

/** The part of a canvas that the context loss watch uses. A test gives a fake. */
export interface ContextEventTarget {
  addEventListener(
    type: "webglcontextlost" | "webglcontextrestored",
    listener: (event: { preventDefault(): void }) => void,
  ): void;
}

/**
 * Watch the canvas for a lost graphics context (the GPU reset, or the browser freed the context).
 * On a loss the page cancels the default, pauses the game and shows a short message. three.js
 * builds its state, its buffers and its textures again when the context comes back (it has its
 * own restore handler on the canvas), so a restore takes the message away. The game stays
 * paused: the player resumes it. A page that never gets the restore keeps the message.
 */
export function bindContextLoss(
  canvas: ContextEventTarget,
  pause: () => void,
  showError: (text: string, title?: string) => void,
  clearError: (text: string) => void,
): void {
  canvas.addEventListener("webglcontextlost", (event) => {
    // Without this the browser never fires the restored event.
    event.preventDefault();
    pause();
    showError(CONTEXT_LOST_TEXT, CONTEXT_LOST_TITLE);
  });
  canvas.addEventListener("webglcontextrestored", () => clearError(CONTEXT_LOST_TEXT));
}

/** The parts of the alert dialog. A test gives fakes. */
export interface AlertParts {
  readonly dialog: HTMLDialogElement;
  readonly title: HTMLElement;
  readonly text: HTMLElement;
  readonly reload: HTMLButtonElement;
}

/** The alert dialog: one modal with a heading, a message and a Reload button. */
export interface AlertDialog {
  /**
   * Show the heading and the text. The dialog opens last, so it is the top of the top layer. The
   * heading is GAME_STOPPED_TITLE when the caller gives none.
   */
  show(text: string, title?: string): void;
  /** Take the alert away only while this text is still on show. */
  clear(text: string): void;
  /** True while an alert is on show. */
  active(): boolean;
  /**
   * Make the alert the top modal again when another dialog opened above it. Close and open it,
   * and keep the focus on Reload. Does nothing when no alert is on show.
   */
  raise(): void;
}

/** The text on show. Null when no alert is on show. */
interface AlertState {
  shown: string | null;
}

/**
 * Make the alert dialog. A modal dialog covers the rest of the page, and the last modal that
 * opens sits on top. So the alert opens after any other dialog, and nothing hides it. While an
 * alert is on show, Escape cannot close it: a close that gets through opens it again. The focus
 * goes to the Reload button. Only `clear` takes the alert away (the graphics came back).
 */
export function bindAlertDialog(parts: AlertParts, reload: () => void): AlertDialog {
  const { dialog, title, text, reload: button } = parts;
  const state: AlertState = { shown: null };
  dialog.addEventListener("cancel", (event) => keepAlertOnCancel(event, state));
  dialog.addEventListener("close", () => reopenAlert(parts, state));
  button.addEventListener("click", reload);
  return {
    show(message, heading = GAME_STOPPED_TITLE) {
      state.shown = message;
      title.textContent = heading;
      text.textContent = message;
      // Close first when it is open: a dialog that opened after it must not stay on top.
      closeAlert(dialog);
      openAlert(dialog, button);
    },
    clear: (message) => clearAlert(parts, state, message),
    active: () => state.shown !== null,
    raise: () => raiseAlert(parts, state),
  };
}

/** Open the alert when it is closed. The focus goes to Reload. */
function openAlert(dialog: HTMLDialogElement, button: HTMLButtonElement): void {
  if (!dialog.open) dialog.showModal();
  button.focus();
}

/** Close the alert when it is open. */
function closeAlert(dialog: HTMLDialogElement): void {
  if (dialog.open) dialog.close();
}

/** A cancel (Escape) does nothing while an alert is on show. */
function keepAlertOnCancel(event: Event, state: AlertState): void {
  if (state.shown !== null) event.preventDefault();
}

/** A close that gets through opens the alert again, while it is on show. */
function reopenAlert(parts: AlertParts, state: AlertState): void {
  if (state.shown !== null) openAlert(parts.dialog, parts.reload);
}

/** Take the alert away only while this text is still on show. */
function clearAlert(parts: AlertParts, state: AlertState, message: string): void {
  const { shown } = state;
  if (shown !== message) return;
  state.shown = null;
  parts.text.textContent = "";
  closeAlert(parts.dialog);
}

/** Make the alert the last modal again. Does nothing when no alert is on show. */
function raiseAlert(parts: AlertParts, state: AlertState): void {
  if (state.shown === null) return;
  // Close and open again: the alert becomes the last modal, so it is on top.
  closeAlert(parts.dialog);
  openAlert(parts.dialog, parts.reload);
}

/** Start the game page on a loaded document. */
export function bootGamePage(doc: Document, win: Win): void {
  const q: Finder = (name) => slot(doc, name);
  const stage = q("stage");
  const errorBox = q("error");
  const dialogs = findDialogs(doc);
  // A message that needs a reload goes in the alert dialog. The playfield box sits under the
  // backdrop of any open modal, so it carries only the start up message (no dialog is open then).
  const notice = bindAlertDialog(
    {
      dialog: q<HTMLDialogElement>("dlg-alert"),
      title: q("alert-title"),
      text: q("alert-text"),
      reload: q<Btn>("alert-reload"),
    },
    () => win.location.reload(),
  );
  // Another dialog can open after the alert. One example is the featured panel after a ticket.
  // That dialog would cover the alert. The hook raises the alert again, so it stays on top.
  // With no alert on show, the hook does nothing.
  setAfterOpenHook(notice.raise);
  const table = loadVibe01Table();

  const motion = { reduced: initialReducedMotion(win) };
  const reducedBox = q<HTMLInputElement>("set-reduced");
  reducedBox.checked = motion.reduced;
  doc.documentElement.setAttribute("data-reduced-motion", String(motion.reduced));
  const view = createView(q<HTMLCanvasElement>("canvas"), table, motion);
  if (view === null) {
    errorBox.textContent =
      "This browser cannot draw the table. The game needs WebGL 2. Try another browser.";
    errorBox.hidden = false;
    closeDialog(dialogs.start);
    return;
  }
  const gauge = createPowerGauge(
    { bar: asBar(q("power-bar")), text: asText(q("power-text")) },
    table.plunger,
  );
  const { hud, feed } = createHudAndFeed(q);
  fillHowTo(q);
  bindPageMenu(doc, q("menu"), q("menu-toggle"), q("menu-list"));

  // One bus, one settings store and one flash limiter for the page.
  const platform = platformOf(win);
  const core = createFeedbackCore(table, platform);
  const parts = createRunParts({
    doc,
    q,
    core,
    isIdle: () => flow.isIdle(),
    modalOpen: () => modalOpen(dialogs) || notice.active(),
  });
  const inputs = createInputs({
    draftRoute: draftRouter(parts.draft),
    doc,
    win,
    q,
    stage,
    dialogs,
    alertOpen: notice.active,
    flow: () => flow,
    commandFor: (code) => keys.commandFor(code),
    core,
    onStart: () => {
      layers.unlock();
      flow.startFree();
    },
  });
  const layers = createFeedbackLayers({
    core,
    view,
    touch: inputs.touch,
    doc,
    createContext: audioContextFactory(win),
    now: platform.now,
    stream: { host: asStreamHost(doc.body), location: win.location, win },
    storage: platform.storage,
    runView: parts.view,
    onSave: parts.showLevels,
    onError: (layer, error) => win.console.warn(`pinball: the ${layer} layer stopped`, error),
  });
  bindReducedMotion(doc, win, reducedBox, motion, { view, gauge, layers });
  bindFeedbackSettings(dialogs.settings, core.store);
  const keys = bindKeySettings({
    doc,
    list: q("set-keys"),
    note: q("set-keys-note"),
    summary: q("start-keys"),
    dialog: dialogs.settings,
    resetButton: q<Btn>("set-keys-reset"),
    store: storageOf(win),
    // A key that is held while its binding changes would stay down. Lift every action first.
    onChange: () => inputs.keyboard.releaseAll(),
  });
  const panel = createFeaturedPanel({
    doc,
    win,
    dialog: dialogs.featured,
    feed,
    isGameIdle: () => flow.isIdle(),
    showDialog: () => openDialog(dialogs.featured),
    onTurnReady: (resume, name) => flow.turnReady(resume, name),
    onTurnEnd: (note) => flow.turnEnd(note),
  });
  // The rules emit their feedback events (a bumper, a jackpot) into the one bus.
  // One seed for the engine and the draft. Each engine has its own run director.
  const makeEngine = (): PinballEngine => {
    const seed = newSeed(win);
    const run = parts.newRun(seed);
    return createEngine(
      table,
      createVibe01Rules({ feedback: core.bus, positionOf: core.positionOf, run }),
      { seed },
    );
  };
  const flow = createGameFlow({
    win,
    doc,
    stage,
    dialogs,
    hud,
    feed,
    view,
    gauge,
    session: panel.session,
    makeEngine,
    openFeatured: () => panel.open(),
    releaseInputs: () => inputs.releaseAll(),
    pollPads: () => inputs.gamepad.poll(),
    overScore: q("over-score"),
    overNote: q("over-note"),
    overBest: q("over-best"),
    overStages: asList(q("over-stages")),
    run: parts.port,
    showError: notice.show,
    locked: notice.active,
    feedback: layers,
  });

  // The audio starts at the first gesture of the player. A gesture on a dialog button counts too.
  inputs.touch.onFirstGesture(layers.unlock);
  const gesture = (): void => {
    layers.unlock();
    inputs.touch.notifyGesture();
  };
  bindButtons(q, dialogs, flow, panel, gesture);
  // The pause runs first. The alert opens after it, so it sits above the pause dialog and above
  // any other dialog that is open. A restore takes the alert away. The game stays paused.
  bindContextLoss(q<HTMLCanvasElement>("canvas"), () => flow.pause(), notice.show, notice.clear);
  wireDialogs(doc, dialogs, stage);
  // A pause or a tab switch must not take the focus from the cards.
  // wireDialogs puts the focus on the stage. This code runs after wireDialogs.
  const refocus = (): void => {
    if (!modalOpen(dialogs)) parts.refocusDraft();
  };
  dialogs.pause.addEventListener("close", refocus);
  doc.addEventListener("visibilitychange", () => {
    if (!doc.hidden) refocus();
  });
  // A draft holds the engine. A key that is down when it opens must not stay down.
  const stopInputWatch = core.bus.subscribe((ev) => {
    if (ev.kind === "draft_open") inputs.releaseAll();
  });
  // The boss sounds and four table moments have a text cue for a screen reader: the live region
  // says it. No box shows.
  const stopLiveText = routeLiveText(core.bus, feed);
  // Both listeners leave together at pagehide.
  const stopDraftWatch = (): void => {
    stopInputWatch();
    stopLiveText();
  };
  parts.showLevels(layers.save());
  bindResize(win, q("playfield"), view, () => flow);
  // A page that leaves for good removes the layers. A page in the back-forward cache keeps them.
  win.addEventListener("pagehide", (e) => {
    if (e.persisted) return;
    stopDraftWatch();
    inputs.touch.dispose();
    layers.dispose();
  });

  // First screen. A link to /claim or #claim opens the featured dialog on the claim form. The start
  // dialog stays below it, so closing the claim dialog leads back to the start screen.
  flow.begin();
  panel.start();
  openDialog(dialogs.start);
  if (wantsClaimDialog(win.location)) panel.openForClaim();
}
